import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  Col,
  Collapse,
  Flex,
  Input,
  Row,
  Select,
  Tabs,
  Typography,
} from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { FormattedMessage, injectIntl } from 'react-intl'
import { isEmpty } from 'lodash'
import { ProblemDescription } from './ProblemDescription'
import { PossibleRootCauses } from './PossibleRootCauses'
import { DirectRootCauses } from './DirectRootCauses'
import { Actions } from './Actions'
import {
  useFetchNewIssueIdForProblemLog,
  useIsCompressedIssueCreationInterface,
  useLinkProblemLogToIssue,
  usePostIssue,
} from '../hooks/Issues'
import { useGetUsers, useUserInfo, useUserPlant } from '../hooks/User'
import { requireAuthentication } from '../helpers/authentication'
import { buildFaultDescription } from './utils'
import './styles.scss'

const { Title, Text } = Typography

// This is the "Add New Issue" page reached from the Incident List's
// AssociateIssuePage. Its tabs mirror the Issue Edit form's ISSUE_CREATION_INTERFACE
// setting: 'compressed' shows the trimmed-down 4M/Actions tabs from Figma
// (see https://www.figma.com/proto/ITEOBzG70wVkwRkep3rc0B/Tracer---Ant-Design?node-id=11772-19253),
// 'full' (default) restores the Problem Description / Direct Root Causes tabs too.
const buildEmptyIssue = (fault, issueCategoryId, plant) => ({
  problem_log_id: fault.id,
  issue_category_id: issueCategoryId,
  plant_id: plant,
  description: buildFaultDescription(fault),
  possible_root_causes: {
    man: null,
    machine: null,
    method: null,
    material: null,
    design: null,
  },
  actions: [
    {
      assignee: null,
      type: null,
      description: null,
      start_date: null,
      due_date: null,
      close_date: null,
      status: 'open',
    },
  ],
  status: 'open',
  clean_point: null,
  responsible: null,
  customer_reference: null,
})

function RepairDetails({ fault, intl }) {
  const items = [
    {
      key: 'repair-details',
      label: intl.formatMessage({
        id: 'incidentMgmt.addNewIssue.repairDetails',
        defaultMessage: 'Repair Details',
      }),
      children: (
        <Flex vertical gap={4}>
          <Text>
            <strong>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.annotation',
                defaultMessage: 'Annotation',
              })}
              :
            </strong>{' '}
            {fault.note}
          </Text>
          <Text>
            <strong>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.problemCode',
                defaultMessage: 'Problem Code',
              })}
              :
            </strong>{' '}
            {fault.problem_code || fault.type}
          </Text>
          <Text>
            <strong>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.modulesDtcs',
                defaultMessage: 'Modules - DTCs',
              })}
              :
            </strong>{' '}
            {(fault.DTCs || [])
              .map((dtc) => `${dtc.acronym} - ${dtc.dtc}`)
              .join(', ')}
          </Text>
          <Text>
            <strong>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.station',
                defaultMessage: 'Station',
              })}
              :
            </strong>{' '}
            {fault.station_id || 'NA'}
          </Text>
        </Flex>
      ),
    },
  ]
  return <Collapse items={items} defaultActiveKey="repair-details" />
}

function AddNewIssue({ intl }) {
  const history = useHistory()
  const location = useLocation()
  const fault = location.state?.fault
  // Present when this page was reached from the Production Line Dashboard's
  // Associate Issue page (see AssociateIssuePage.js's handleCreateNewIssue) --
  // needed so "Go Back" and a successful submit return to the right place
  // instead of always landing in the Incident List's associate flow.
  const vehicleTest = location.state?.vehicleTest
  const issueCategoryId = location.state?.issueCategoryId
  const [plant] = useUserPlant()

  const [issue, setIssue] = useState(() =>
    fault ? buildEmptyIssue(fault, issueCategoryId, plant) : null,
  )
  const isCompressed = useIsCompressedIssueCreationInterface()
  // Target the full-mode first tab key here; if isCompressed turns out true,
  // 'problem-desc' won't be a valid key and currentTab below falls back to
  // whatever the first tab actually is (avoids landing mid-tab-list either way).
  const [activeTab, setActiveTab] = useState('problem-desc')
  const [issueId, setIssueId] = useState(null)

  const fetchNewIssueId = useFetchNewIssueIdForProblemLog()
  const postIssue = usePostIssue()
  const linkProblemLogToIssue = useLinkProblemLogToIssue()
  const { users } = useGetUsers()
  const { userInfo } = useUserInfo()

  const responsibleOptions = (users ?? [])
    .filter((user) => !user.disabled && !isEmpty(user.email))
    .map((user) => ({ value: user.email, label: user.email }))

  useEffect(() => {
    if (!fault || issueCategoryId == null) return
    fetchNewIssueId.mutate(
      { problem_log_id: fault.id, issue_category_id: issueCategoryId },
      { onSuccess: (id) => setIssueId(id) },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fault?.id, issueCategoryId])

  useEffect(() => {
    if (issue && !issue.responsible && userInfo) {
      setIssue({ ...issue, responsible: userInfo.username })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo])

  // plant is captured once in the buildEmptyIssue() initializer above, but
  // useUserPlant() can still be null on first render for users without a
  // cached plant -- sync it in once it resolves, same as responsible above.
  useEffect(() => {
    if (issue && !issue.plant_id && plant) {
      setIssue({ ...issue, plant_id: plant })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant])

  if (!fault || issueCategoryId == null || !issue) {
    return (
      <div id="add-new-issue-page">
        <FormattedMessage
          id="incidentMgmt.addNewIssue.missingContext"
          defaultMessage="No fault/category selected -- go back and select a category first."
        />
      </div>
    )
  }

  const goBack = () =>
    vehicleTest
      ? history.push(`/production-line-dashboard/associate/${vehicleTest.id}`, {
          vehicleTest,
        })
      : history.push(`/incidents/associate/${fault.id}`, { fault })

  const submitIssue = () => {
    postIssue.mutate(
      { issue },
      {
        onSuccess: ({ issue_id }) => {
          linkProblemLogToIssue.mutate(
            { issue_id, pl_id: fault.id },
            {
              onSuccess: () =>
                history.push(
                  vehicleTest ? '/production-line-dashboard' : '/incidents',
                ),
            },
          )
        },
      },
    )
  }

  // 'compressed' shows just the 4M + Actions tabs from Figma; 'full' restores
  // the original Problem Description / Direct Root Causes tabs too, matching
  // the Issue Edit form. See ISSUE_CREATION_INTERFACE.
  const tabItems = [
    ...(!isCompressed
      ? [
          {
            key: 'problem-desc',
            label: intl.formatMessage({
              id: 'incidentMgmt.addNewIssue.problemDescription',
              defaultMessage: 'Problem Description',
            }),
            children: (
              <ProblemDescription
                issue={issue}
                setIssue={setIssue}
                intl={intl}
              />
            ),
          },
        ]
      : []),
    {
      key: '4m',
      label: isCompressed
        ? intl.formatMessage({
            id: 'incidentMgmt.addNewIssue.fourM',
            defaultMessage: '4M',
          })
        : intl.formatMessage({
            id: 'incidentMgmt.addNewIssue.possibleRootCauses',
            defaultMessage: 'Possible Root Causes',
          }),
      children: (
        <PossibleRootCauses issue={issue} setIssue={setIssue} intl={intl} />
      ),
    },
    ...(!isCompressed
      ? [
          {
            key: 'direct-root-cause',
            label: intl.formatMessage({
              id: 'incidentMgmt.addNewIssue.directRootCauses',
              defaultMessage: 'Direct Root Causes',
            }),
            children: (
              <DirectRootCauses issue={issue} setIssue={setIssue} intl={intl} />
            ),
          },
        ]
      : []),
    {
      key: 'actions',
      label: intl.formatMessage({
        id: 'incidentMgmt.addNewIssue.actions',
        defaultMessage: 'Actions',
      }),
      children: (
        <Flex vertical gap={10}>
          <Flex vertical={false} align="center" gap="small">
            <Text strong>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.responsible',
                defaultMessage: 'Responsible',
              })}
              :
            </Text>
            <Select
              style={{ minWidth: 240 }}
              showSearch
              options={responsibleOptions}
              value={issue.responsible ?? null}
              onChange={(value) => setIssue({ ...issue, responsible: value })}
              placeholder={intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.responsiblePlaceholder',
                defaultMessage: 'Select responsible user',
              })}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Flex>
          <Flex vertical={false} align="center" gap="small">
            <Text strong>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.customerReference',
                defaultMessage: 'Customer Reference',
              })}
              :
            </Text>
            <Input
              value={issue.customer_reference ?? ''}
              onChange={(evt) =>
                setIssue({ ...issue, customer_reference: evt.target.value })
              }
            />
          </Flex>
          <Actions issue={issue} setIssue={setIssue} intl={intl} />
        </Flex>
      ),
    },
  ]
  const tabKeys = tabItems.map((item) => item.key)
  // isCompressed loads asynchronously (from userInfo) and can change which
  // tab keys exist -- fall back to the first tab if activeTab no longer
  // matches one, rather than rendering with no tab selected.
  const currentTab = tabKeys.includes(activeTab) ? activeTab : tabKeys[0]

  return (
    <div id="add-new-issue-page">
      <Flex align="center" gap="middle" className="add-new-issue-header">
        <Button icon={<ArrowLeftOutlined />} shape="round" onClick={goBack}>
          <FormattedMessage
            id="incidentMgmt.addNewIssue.goBack"
            defaultMessage="Go Back"
          />
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          <FormattedMessage
            id="incidentMgmt.addNewIssue.title"
            defaultMessage="Add New Issue"
          />
        </Title>
      </Flex>

      <Row gutter={24} className="add-new-issue-body">
        <Col span={16} className="add-new-issue-left-col">
          <Tabs
            type="card"
            items={tabItems}
            activeKey={currentTab}
            onChange={setActiveTab}
          />
          <Flex gap={10} style={{ marginTop: 16 }}>
            {currentTab !== tabKeys[tabKeys.length - 1] && (
              <Button
                type="primary"
                onClick={() =>
                  setActiveTab(tabKeys[tabKeys.indexOf(currentTab) + 1])
                }
              >
                <FormattedMessage
                  id="incidentMgmt.addNewIssue.next"
                  defaultMessage="Next"
                />
              </Button>
            )}
            {currentTab !== tabKeys[0] && (
              <Button
                onClick={() =>
                  setActiveTab(tabKeys[tabKeys.indexOf(currentTab) - 1])
                }
              >
                <FormattedMessage
                  id="incidentMgmt.addNewIssue.previous"
                  defaultMessage="Previous"
                />
              </Button>
            )}
            <Button type="primary" onClick={submitIssue}>
              <FormattedMessage
                id="incidentMgmt.addNewIssue.finish"
                defaultMessage="Finish"
              />
            </Button>
          </Flex>
        </Col>
        <Col span={8} className="add-new-issue-right-col">
          <Text strong>{issueId}</Text>
          <div>
            <Text>
              {intl.formatMessage({
                id: 'incidentMgmt.addNewIssue.issueDescription',
                defaultMessage: 'Issue Description',
              })}
              :
            </Text>
            <Input
              value={issue.description ?? ''}
              onChange={(evt) =>
                setIssue({ ...issue, description: evt.target.value })
              }
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <RepairDetails fault={fault} intl={intl} />
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default requireAuthentication(injectIntl(AddNewIssue))
